import type { ComponentProps, MouseEvent } from "react";
import { cn } from "@/lib/utils";

/** Elements inside a row that keep their own click behavior. */
const INTERACTIVE =
  "a,button,input,select,textarea,label,summary,[role=checkbox],[role=button],[role=menuitem],[data-row-ignore]";

/** The row's single real link. Rows mark it with `data-row-link`. */
export const ROW_LINK_SELECTOR = "a[data-row-link]";

function endsTextSelection(row: HTMLElement): boolean {
  const selection = typeof window.getSelection === "function" ? window.getSelection() : null;
  if (!selection || selection.isCollapsed || selection.toString() === "") return false;
  return (
    (selection.anchorNode !== null && row.contains(selection.anchorNode)) ||
    (selection.focusNode !== null && row.contains(selection.focusNode))
  );
}

function ignored(event: MouseEvent<HTMLElement>): boolean {
  if (event.defaultPrevented) return true;
  const target = event.target;
  if (!(target instanceof Element)) return true;
  // React clicks bubble through portals (menus, dialogs): the target is not inside the row's DOM.
  if (!event.currentTarget.contains(target)) return true;
  const interactive = target.closest(INTERACTIVE);
  return interactive !== null && event.currentTarget.contains(interactive);
}

/**
 * Makes a whole table row (or card) act as its `a[data-row-link]` without a positioned overlay.
 * WebKit does not apply `position: relative` to `<tr>`, so stretched-link overlays inside rows
 * span the page there. The row keeps one real link (focus, name, copy link) and forwards clicks
 * on the rest of the row to it; middle and modifier clicks open the target in a new tab.
 */
export function useRowLink({ newTab = true }: { newTab?: boolean } = {}) {
  return {
    onClick(event: MouseEvent<HTMLElement>) {
      if (event.button !== 0 || ignored(event)) return;
      const row = event.currentTarget;
      if (endsTextSelection(row)) return;
      const link = row.querySelector<HTMLAnchorElement>(ROW_LINK_SELECTOR);
      if (!link) return;
      if (newTab && (event.ctrlKey || event.metaKey || event.shiftKey)) {
        window.open(link.href, "_blank", "noopener");
        return;
      }
      if (event.altKey) return;
      // Without `newTab` (selection mode) modifier clicks are forwarded as plain clicks, like the title link.
      link.click();
    },
    onAuxClick(event: MouseEvent<HTMLElement>) {
      if (event.button !== 1 || ignored(event)) return;
      const link = event.currentTarget.querySelector<HTMLAnchorElement>(ROW_LINK_SELECTOR);
      if (!link) return;
      event.preventDefault();
      window.open(link.href, "_blank", "noopener");
    },
  };
}

export const ROW_LINK_CLASS =
  "cursor-pointer has-[a[data-row-link]:focus-visible]:outline-2 has-[a[data-row-link]:focus-visible]:-outline-offset-2 has-[a[data-row-link]:focus-visible]:outline-ring";

/** A `<tr>` that behaves as one link target (see `useRowLink`). Never position it. */
export function RowLinkRow({
  className,
  newTab,
  ...props
}: ComponentProps<"tr"> & { newTab?: boolean }) {
  const handlers = useRowLink({ newTab });
  return <tr className={cn(ROW_LINK_CLASS, className)} {...handlers} {...props} />;
}

/** The same behavior for any other row-like element (cards, list items). */
export function RowLinkItem({ className, ...props }: ComponentProps<"li">) {
  const handlers = useRowLink();
  return <li className={cn(ROW_LINK_CLASS, className)} {...handlers} {...props} />;
}
